import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import CringeShell from './CringeShell';
import { GameHead, Popup, TopBar, useSaveScore } from './common';
import { useGameTimer } from './hooks';

// Clips are served from S3 next to the other game assets (<REACT_APP_S3_PATH>audioN.mp3); the copy bundled with
// the app is the fallback if the S3 file can't be loaded.
const S3_BASE = process.env.REACT_APP_S3_PATH;
const s3Audio = (file) => (S3_BASE ? `${S3_BASE.replace(/\/?$/, '/')}${file}` : `/cringe/sounds/${file}`);

const CLIPS = [
  { id: 'disk1', img: 'audio_disk1.png', audio: s3Audio('audio1.mp3'), options: ['Joy to the World', 'Jingle Bells'], answer: 'Jingle Bells' },
  { id: 'disk2', img: 'audio_disk2.png', audio: s3Audio('audio2.mp3'), options: ['O Come All Ye Faithful', 'Silent Night'], answer: 'Silent Night' },
  { id: 'disk3', img: 'audio_disk1.png', audio: s3Audio('audio3.mp3'), options: ['Deck the Halls', 'Drummer Boy'], answer: 'Deck the Halls' },
];
const CLIP_MS = 10000; // each clip plays for 10 seconds before the question

const RIGHT = 'Jingle bells all the way! You surely know your Christmas songs!';
const WRONG = 'Try again, these are Christmas carols you’ve probably heard before Cringe created this mess!';

function Stage3Game() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const game = useSelector((s) => s.cringe.game);
  const timer = useGameTimer(true, game?.time);
  const save = useSaveScore();
  const audioRefs = useRef({});
  const pointsRef = useRef(game?.totalScore || 0);
  const [points, setPoints] = useState(pointsRef.current);
  const [done, setDone] = useState([]); // disks already answered (hidden)
  const [playing, setPlaying] = useState(null);
  const [question, setQuestion] = useState(null);
  const [message, setMessage] = useState(null);
  const timeouts = useRef([]);

  useEffect(
    () => () => {
      timeouts.current.forEach(clearTimeout);
      Object.values(audioRefs.current).forEach((a) => a && a.pause());
    },
    []
  );

  const play = (clip) => {
    if (playing || question || done.includes(clip.id)) return;
    const audio = audioRefs.current[clip.id];
    setPlaying(clip.id);
    audio.currentTime = 0;
    audio.play().catch((e) => console.warn('Clip could not play:', e.message));
    timeouts.current.push(
      setTimeout(() => {
        audio.pause();
        audio.currentTime = 0;
        setPlaying(null);
        setQuestion(clip);
      }, CLIP_MS)
    );
  };

  const answer = (option) => {
    const clip = question;
    setQuestion(null);
    if (option === clip.answer) {
      pointsRef.current += 1;
      setPoints(pointsRef.current);
      setMessage(RIGHT);
    } else {
      setMessage(WRONG);
    }
    const nowDone = [...done, clip.id];
    setDone(nowDone);
    // Every answer is saved (question-insert.php, question 3); after the third it's on to the pipes.
    save(3, pointsRef.current, timer.current(), { quiet: true }).then(() => {
      if (nowDone.length === CLIPS.length) {
        timeouts.current.push(setTimeout(() => navigate({ pathname: '/stage4', search }), 1500));
      }
    });
  };

  return (
    <>
      <TopBar time={timer.text} points={points} />
      <div className="cr-stage-body">
        <GameHead>
          Cringe mixed up Frostvale’s holiday playlist, leaving scrambled fragments of familiar holiday tunes. You must
          identify the songs from short audio clips to set the playlist right.
        </GameHead>
      </div>

      <div className="cr-stage3-scene">
        {CLIPS.map((clip) =>
          done.includes(clip.id) || playing === clip.id || question?.id === clip.id ? null : (
            <button
              key={clip.id}
              type="button"
              className={`cr-img-btn cr-disk cr-${clip.id}`}
              onClick={() => play(clip)}
            >
              <img src={`/cringe/img/stage3/${clip.img}`} alt={`Audio ${clip.id}`} />
            </button>
          )
        )}
        <div className="cr-gramophone">
          <img
            src={playing ? '/cringe/img/stage3/gramophone-min.gif' : '/cringe/img/stage3/gramophone.png'}
            alt="Gramophone"
          />
        </div>
      </div>

      {CLIPS.map((clip) => (
        <audio
          key={clip.id}
          ref={(el) => (audioRefs.current[clip.id] = el)}
          src={clip.audio}
          preload="auto"
          onError={(e) => {
            const local = `/cringe/sounds/${clip.audio.split('/').pop()}`;
            if (!e.currentTarget.src.endsWith(local)) e.currentTarget.src = local;
          }}
        />
      ))}

      {question && (
        <div className="cr-overlay">
          <div className="cr-quiz" style={{ backgroundImage: 'url(/cringe/img/popup.png)' }}>
            <p>Which song is this?</p>
            <div className="cr-quiz-options">
              {question.options.map((o) => (
                <button key={o} type="button" className="cr-option" onClick={() => answer(o)}>
                  {o}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {message && (
        <Popup onClose={() => setMessage(null)}>
          <h3>{message}</h3>
        </Popup>
      )}
    </>
  );
}

// stage3.php: play each record on the gramophone and name the carol — 1 point each.
export default function CringeStage3() {
  return (
    <CringeShell bg="stage3/stage3_desk.png" bgMob="stage3/stage3_mob.png" stage={3} className="cr-stage cr-stage3">
      <Stage3Game />
    </CringeShell>
  );
}
